import React, { useEffect, useMemo, useState } from 'react';
import { EMPTY_IMAGE, Table, Typography } from '../../components/common';
import toast from '../../components/common/toast';
import { GetUsers } from '../../services/Api/users/useApiUsers';
import { GetInstances } from '../../services/Api/instances/useApiInstances';
import { useGetUser } from '../../contexts/user';
import NewUserModal from '../../components/User/Modals/NewUserModal';
import Avatar from '../../components/User/Avatar';
import { Controls, getRowProps } from '../../components/common/table/tableHelpers';
import { useHistory } from 'react-router-dom';
import { GetUserGroups } from '../../services/Api/userGroups/useApiUserGroups';

const Users = () => {
    const { hasRolePermission } = useGetUser();
    const [groups, setGroups] = useState([]);
    const [instances, setInstances] = useState({count: -1, loaded: 0});
    const [users, setUsers] = useState([]);
    const [newUserInfo, setNewUserInfo] = useState({});
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);

    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);

    const canAddUser = hasRolePermission('ac');

    const openNewUserForm = (values = {}) => {
        setNewUserInfo(values);
        setShowCreateUserForm(true);
    };

    const history = useHistory();

    useEffect(() => {
        setInstances({count: 0, loaded: 0});

        GetUserGroups()
            .then((response) => {
                if (response.data) {
                    setGroups(response.data.map((g) => ({ value: g.id, label: g.name })));
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error('Could not load teams');
            });

        GetInstances()
            .then((instancesResponse) => { 
                if (instancesResponse.data) {
                    setInstances(current => ({...current, count: instancesResponse.data.length}));
                    instancesResponse.data.forEach(instance => {
                        GetUsers({instanceId: instance.Id})
                            .then((instanceUsersResponse) => { 
                                setInstances(current => ({...current, loaded: current.loaded + 1}));
                                const instanceUsers = instanceUsersResponse?.data?.map((user) => ({
                                    ...user,
                                    name:
                                        user.user && user.user.firstname
                                            ? `${user.user.firstname || ''} ${user.user.surname}`
                                            : user.Username,
                                    email: user.Username,
                                    role: user.user?.roleId,
                                    rolename: user.user?.rolename,
                                    roledescription: user.user?.roledescription,
                                    imgUrl: EMPTY_IMAGE.src,
                                    id: user.Id,
                                    isUser: !!user.user,
                                    groupids: user.user?.groupids || []
                                }));
                                setUsers(u => {
                                    const newUsers = instanceUsers.filter(i => !u.map(u => u.Username).includes(i.Username));
                                    return [...u, ...newUsers];
                                });
                            }).catch((e) => { 
                                setInstances(current => ({...current, loaded: current.loaded + 1}));
                            });
                    });
                }
            }).catch((e) => {
                toast.error();
                console.error(e);
                setInstances(current => ({...current, count: 0, loaded: 0}));
            });
        
    }, []);

    const roleOptions = users?.map((user) => ({
        label: user.rolename,
        value: user.rolename,
    }));

    const uniqueRoleOptions = [
        ...new Map(roleOptions.map((item) => [item['label'], item])).values(),
    ].filter((role) => role.value !== undefined);

    const onRoleChange = (e) => {
        setSelectedRole(e);
        const useUsers = users.filter((user) =>
            selectedGroup ? user.groupids?.includes(selectedGroup?.value) : true
        );
        setFilteredUsers(useUsers.filter((user) => (e ? user.rolename === e?.value : true)));
    };

    const onGroupChange = (e) => {
        setSelectedGroup(e);
        const useUsers = users.filter((user) =>
            selectedRole ? user.rolename === selectedRole?.value : true
        );
        setFilteredUsers(useUsers.filter((user) => (e ? user.groupids?.includes(e?.value) : true)));
    };

    const filters = [
        {
            options: uniqueRoleOptions,
            placeholder: 'Filter by Role',
            onChange: (e) => onRoleChange(e),
            isClearable: true,
        },
        {
            options: groups,
            placeholder: 'Filter by Team',
            onChange: (e) => onGroupChange(e),
            isClearable: true,
        },
    ];

    const controls = (
        <Controls
            filters={filters}
            canAdd={canAddUser}
            onClick={() => openNewUserForm()}
            addText="Add User"
        />
    );

    const onRowClick = (row) => {
        row.values?.isUser
            ? history.push(`/users/${row.values?.email}`)
            : openNewUserForm({ email: row.values?.email, lock: ['email'] });
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Exists',
                accessor: 'isUser',
                hidden: true,
            },
            {
                Header: 'User',
                accessor: 'name',
                Cell: ProfileCell,
                imgAccessor: 'imgUrl',
                emailAccessor: 'email',
            },
            {
                Header: 'Email',
                accessor: 'email',
                Cell: EmailCell,
                hidden: false,
            },
            {
                Header: 'Role',
                accessor: 'rolename',
                Cell: RoleCell,
            },
        ],
        //eslint-disable-next-line
        []
    );

    return (
        <div className="users-page pb-10">
            <Table
                columns={columns}
                data={filteredUsers ? filteredUsers : users}
                isLoading={instances.count === -1 || instances.loaded < instances.count}
                filterable={true}
                filterPlaceholder="Search for Users..."
                controls={controls}
                getRowProps={(row) => getRowProps(row, onRowClick, hoveredRow, setHoveredRow)}
                loadingElement={<meter className="w-full" value={instances.loaded} min={0} max={instances.count} />}
            />
            {!showCreateUserForm || !canAddUser ? (
                <></>
            ) : (
                <NewUserModal
                    defaultValues={newUserInfo}
                    closeForm={() => setShowCreateUserForm(false)}
                />
            )}
        </div>
    );
};

function ProfileCell({ value }) {
    return (
        <div className="flex items-center gap-4">
            <Avatar name={value} />
            <Typography variant="heading3.1">{value}</Typography>
        </div>
    );
}

function EmailCell({ value, row }) {
    return <Typography variant="body1.2">{value}</Typography>;
}

function RoleCell({ value }) {
    return <Typography variant="body1.2">{value}</Typography>;
}

export default Users;
